import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { ChannelPicker, CHANNEL_TYPE } from '@/components/ChannelPicker';
import { RolePicker } from '@/components/RolePicker';
import { Trash2 } from 'lucide-react';
import { DiscordMessagePreview } from '@/components/DiscordMessagePreview';
import { MediaPicker } from '@/components/MediaPicker';
import { useConfigPage, BackLink } from './_shared';

/**
 * Leveling-Config — consumer is `Leveling/events/XP.js` which reads:
 *   leveling.levelUpChannel — where "Level up!" messages go
 *   leveling.roleRewards    — array of {onLevel, roleId}, checked at every level-up
 */
interface RoleReward {
  onLevel: number;
  roleId: string;
}

export function LevelingConfig() {
  const { guildId, settingsQuery, saveMut, currentConfig } = useConfigPage('leveling');
  const [levelUpChannel, setLevelUpChannel] = useState<string | null>(null);
  const [rewards, setRewards] = useState<RoleReward[]>([]);
  const [imageUrl, setImageUrl] = useState('');
  const [newLevel, setNewLevel] = useState('');
  const [newRoleId, setNewRoleId] = useState<string | null>(null);

  useEffect(() => {
    if (settingsQuery.data) {
      setLevelUpChannel((currentConfig.levelUpChannel as string) ?? null);
      setImageUrl((currentConfig.imageUrl as string) ?? '');
      setRewards(
        Array.isArray(currentConfig.roleRewards)
          ? (currentConfig.roleRewards as RoleReward[])
          : []
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsQuery.data]);

  if (settingsQuery.isLoading) return <p className="text-muted-foreground">Lade…</p>;

  const addReward = () => {
    const level = parseInt(newLevel, 10);
    if (!Number.isFinite(level) || level < 1 || !newRoleId) return;
    if (rewards.some((r) => r.onLevel === level)) return;
    setRewards(
      [...rewards, { onLevel: level, roleId: newRoleId }].sort((a, b) => a.onLevel - b.onLevel)
    );
    setNewLevel('');
    setNewRoleId(null);
  };

  const removeReward = (level: number) =>
    setRewards(rewards.filter((r) => r.onLevel !== level));

  return (
    <div className="mx-auto max-w-2xl">
      <BackLink guildId={guildId} />
      <h1 className="mb-6 text-2xl font-semibold">Leveling — Konfiguration</h1>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="text-lg">Level-Up-Channel</CardTitle>
          <CardDescription>Wo „🚀 Level up!"-Nachrichten hingepostet werden.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ChannelPicker
            guildId={guildId}
            value={levelUpChannel}
            onChange={setLevelUpChannel}
            types={[CHANNEL_TYPE.GUILD_TEXT]}
          />
          <div>
            <Label>Großes Bild URL (optional)</Label>
            <div className="flex gap-2">
              <Input
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://…"
              />
              <MediaPicker
                guildId={guildId}
                currentUrl={imageUrl}
                onSelect={(url) => setImageUrl(window.location.origin + url)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Rollen-Belohnungen</CardTitle>
          <CardDescription>
            Wenn ein User dieses Level erreicht, bekommt er automatisch die Rolle.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {rewards.length === 0 && (
            <p className="text-sm text-muted-foreground">Keine Belohnungen konfiguriert.</p>
          )}
          <ul className="space-y-2">
            {rewards.map((r) => (
              <li
                key={r.onLevel}
                className="flex items-center gap-3 rounded-md border p-2 text-sm"
              >
                <span className="w-16 shrink-0">
                  Level <strong>{r.onLevel}</strong>
                </span>
                <span className="min-w-0 flex-1 truncate">
                  → Rolle <code>{r.roleId}</code>
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeReward(r.onLevel)}
                  title="Entfernen"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>

          <div className="grid grid-cols-[100px_1fr_auto] items-end gap-2 border-t pt-4">
            <div>
              <Label>Level</Label>
              <Input
                type="number"
                min={1}
                value={newLevel}
                onChange={(e) => setNewLevel(e.target.value)}
                placeholder="5"
              />
            </div>
            <div>
              <Label>Rolle</Label>
              <RolePicker guildId={guildId} value={newRoleId} onChange={setNewRoleId} />
            </div>
            <Button onClick={addReward} disabled={!newLevel || !newRoleId}>
              + Hinzufügen
            </Button>
          </div>

          <div className="flex items-center justify-end pt-2">
            <Button
              disabled={saveMut.isPending}
              onClick={() =>
                saveMut.mutate({
                  moduleConfig: {
                    levelUpChannel,
                    roleRewards: rewards,
                    imageUrl: imageUrl || null,
                  },
                })
              }
            >
              {saveMut.isPending ? 'Speichere…' : 'Alles speichern'}
            </Button>
          </div>
          {saveMut.isSuccess && <p className="text-sm text-success">Gespeichert ✓</p>}
          {saveMut.error && (
            <p className="text-sm text-destructive">
              Fehler: {(saveMut.error as Error).message}
            </p>
          )}
        </CardContent>
      </Card>

      <div className="mt-6">
        <DiscordMessagePreview
          hint="So sieht die Level-Up-Nachricht im gewählten Channel aus (Text aktuell nicht editierbar)."
          content="{USER}"
          embed={{
            title: '🚀 Level up!',
            description: '🔥 Herzlichen Glückwunsch {USER}, du bist jetzt Level **{LEVEL}**!',
            imageUrl: imageUrl || undefined,
          }}
        />
      </div>
    </div>
  );
}
